import React from 'react';
import { InnerWeatherIcon } from './InnerWeatherIcon';
import { MindsetTrendChart } from '../shared/components/MindsetTrendChart';
import { ChatAboutButton } from '../routes/progress/components/ChatAboutButton';
import { FAQHelpIcon } from '../shared/components/FAQHelpIcon';

interface TrendPoint {
  composite_score: number;
  recorded_at: string;
}

interface InnerWeatherWidgetProps {
  a: number;        // neuroticism (0-100)
  b: number;        // well-being (0-100)
  c: number;        // self-efficacy (0-100)
  variant?: 'compact' | 'full';
  trend?: TrendPoint[];
  className?: string;
  hideDiscuss?: boolean;
}

const CHAT_ABOUT_LABEL = 'Discuss';

/**
 * Composed inner-weather widget: weather icon + optional trend sparkline + chat button.
 *
 * - `compact` (default) — sidebar / small cards: smaller icon, smaller text
 * - `full` — profile page / large cards: bigger icon, pill-styled quote
 */
export const InnerWeatherWidget: React.FC<InnerWeatherWidgetProps> = ({
  a,
  b,
  c,
  variant = 'compact',
  trend,
  className = '',
  hideDiscuss = false,
}) => {
  const showTrend = trend && trend.length >= 2;

  const chatButton = (
    <div className="mt-2 flex items-center justify-between">
      <FAQHelpIcon anchor="progress" size="sm" tooltipText="What is Inner Weather?" />
      {!hideDiscuss && (
        <ChatAboutButton
          contextType="mindset"
          contextId="mindset_scores"
          systemMessage="User tapped Discuss on their Inner Weather. Call get_mindset_scores, then respond in 1-2 sentences. Keep it brief."
          autoSubmitPrompt="I'd like to talk about my inner weather and mindset."
          label={CHAT_ABOUT_LABEL}
          variant="text"
        />
      )}
    </div>
  );

  if (variant === 'full') {
    return (
      <div className={`flex flex-col h-full ${className}`}>
        <div className="flex-1 min-h-0">
          <div className="w-full h-20 mb-2">
            <InnerWeatherIcon a={a} b={b} c={c} className="w-full h-full" />
          </div>
          {showTrend && (
            <div className="text-center">
              <MindsetTrendChart data={trend} />
            </div>
          )}
        </div>
        {chatButton}
      </div>
    );
  }

  // compact variant
  return (
    <div className={className}>
      <div className="w-full h-10 mb-1">
        <InnerWeatherIcon a={a} b={b} c={c} className="w-full h-full" />
      </div>
      {showTrend && (
        <div className="text-center">
          <MindsetTrendChart data={trend} />
        </div>
      )}
      {chatButton}
    </div>
  );
};
